// PRD-0191 Brand → Visuals: the brand visual kit (palette roles, fonts, art direction, card text)
// with a live OG card preview rendered by the backend from the saved kit.
import { useEffect, useState, type ChangeEvent } from "react";
import { PALETTE_ROLES, FONT_ROLES, type BrandVisualKit, type FontRole, type PaletteRole } from "../../../shared/visuals";
import { useImportVisualKit, useKitPreview, useSaveVisualKit, useUploadKitFont, useVisualKit } from "../hooks/useVisuals";
import { LoadErrorState } from "../components/ui/LoadState";

const ROLE_LABELS: Record<PaletteRole, string> = {
  background: "Background", surface: "Surface", text: "Text", muted: "Muted text", accent: "Accent", highlight: "Highlight",
};
const FONT_LABELS: Record<FontRole, string> = { heading: "Headings", body: "Body", mono: "Code" };
const DEFAULT_PALETTE: Record<PaletteRole, string> = { background: "#FFFFFF", surface: "#FFFFFF", text: "#111827", muted: "#6B7280", accent: "#2563EB", highlight: "#7C3AED" };

interface Draft {
  palette: Record<PaletteRole, string>;
  artDirection: string;
  dos: string;
  donts: string;
  eyebrow: string;
  wordmark: string;
}

function draftFrom(kit: BrandVisualKit | null): Draft {
  return {
    palette: { ...DEFAULT_PALETTE, ...(kit?.palette ?? {}) },
    artDirection: kit?.image_style.art_direction ?? "",
    dos: (kit?.image_style.do ?? []).join("\n"),
    donts: (kit?.image_style.dont ?? []).join("\n"),
    eyebrow: kit?.templates.eyebrow ?? "Blog",
    wordmark: kit?.templates.wordmark ?? "",
  };
}

const lines = (value: string) => value.split("\n").map((line) => line.trim()).filter(Boolean).slice(0, 12);

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file"));
    reader.readAsDataURL(file);
  });
}

export function BrandVisuals({ brandId }: { brandId: string }) {
  const kitQ = useVisualKit(brandId);
  const save = useSaveVisualKit(brandId);
  const importTokens = useImportVisualKit(brandId);
  const uploadFont = useUploadKitFont(brandId);
  const kit = kitQ.data ?? null;
  const [draft, setDraft] = useState<Draft>(() => draftFrom(null));
  const [previewTitle, setPreviewTitle] = useState("Why agents need a real browser session");
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  useEffect(() => { if (kitQ.isSuccess) setDraft(draftFrom(kit)); }, [kitQ.isSuccess, kit]);

  const hasRenderFonts = !!kit && ["heading", "body"].every((role) => kit.fonts.some((font) => font.role === role));
  // Debounce the sample title so typing does not render (and store) a card per keystroke.
  const [debouncedTitle, setDebouncedTitle] = useState(previewTitle);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTitle(previewTitle.trim() || "Your post title"), 600);
    return () => clearTimeout(timer);
  }, [previewTitle]);
  const previewQ = useKitPreview(brandId, kit?.version, debouncedTitle, !!kit && hasRenderFonts);
  const previewSrc = previewQ.data?.url ?? null;

  const fail = (error: unknown, fallback: string) => setMessage({ tone: "error", text: error instanceof Error && error.message ? error.message : fallback });

  async function handleSave() {
    setMessage(null);
    try {
      await save.mutateAsync({
        palette: draft.palette,
        image_style: { art_direction: draft.artDirection.trim(), do: lines(draft.dos), dont: lines(draft.donts) },
        templates: { eyebrow: draft.eyebrow.trim() || null, wordmark: draft.wordmark.trim() || null },
        ...(kit ? { expected_version: kit.version } : {}),
      });
      setMessage({ tone: "ok", text: "Visual kit saved." });
    } catch (error) { fail(error, "Could not save the visual kit"); }
  }

  async function handleImport(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setMessage(null);
    try {
      const tokens = JSON.parse(await file.text()) as Record<string, unknown>;
      await importTokens.mutateAsync({ tokens, source: file.name, ...(kit ? { expected_version: kit.version } : {}) });
      setMessage({ tone: "ok", text: `Imported colors and fonts from ${file.name}.` });
    } catch (error) { fail(error, "That file is not a design-tokens JSON"); }
  }

  async function handleFont(role: FontRole, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setMessage(null);
    const family = kit?.typography[role] ?? file.name.replace(/[-_]?(\d{3}|regular|bold|latin|italic).*$/i, "").replace(/[-_]/g, " ").trim();
    try {
      await uploadFont.mutateAsync({ role, family: family || file.name, filename: file.name, data_base64: await readAsBase64(file),
        ...(/italic/i.test(file.name) ? { style: "italic" as const } : {}), ...(kit ? { expected_version: kit.version } : {}) });
      setMessage({ tone: "ok", text: `${FONT_LABELS[role]} font uploaded.` });
    } catch (error) { fail(error, "Could not upload the font"); }
  }

  if (kitQ.isLoading) return <div className="text-sm text-slate-500">Loading visual kit…</div>;
  if (kitQ.isError) return <LoadErrorState title="The visual kit could not be loaded" description="Try again in a moment." onRetry={() => void kitQ.refetch()} />;
  const busy = save.isPending || importTokens.isPending || uploadFont.isPending;
  const weak = kit?.contrast.filter((pair) => !pair.passes_aa) ?? [];

  return (
    <div className="space-y-4" data-testid="brand-visuals">
      <div className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">Visual kit</h3>
        <p className="mt-1 text-sm text-slate-600">
          Colors, fonts and art direction used for blog hero images and social cards.
          {kit ? ` Version ${kit.version}${kit.source ? ` · from ${kit.source}` : ""}.` : " Not set up yet."}
        </p>
        <label className="mt-3 inline-flex cursor-pointer items-center rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50">
          Import design tokens (JSON)
          <input type="file" accept="application/json,.json" className="sr-only" onChange={(event) => void handleImport(event)} disabled={busy} />
        </label>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4 rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
          <fieldset>
            <legend className="text-sm font-semibold text-slate-900">Palette</legend>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {PALETTE_ROLES.map((role) => (
                <label key={role} className="flex items-center gap-2 text-xs text-slate-700">
                  <input type="color" value={draft.palette[role]} aria-label={`${ROLE_LABELS[role]} color`}
                    onChange={(event) => setDraft({ ...draft, palette: { ...draft.palette, [role]: event.target.value.toUpperCase() } })}
                    className="h-7 w-9 rounded border border-slate-300" />
                  <span>{ROLE_LABELS[role]} <span className="font-mono text-slate-400">{draft.palette[role]}</span></span>
                </label>
              ))}
            </div>
            {weak.length > 0 && (
              <p className="mt-2 text-xs text-amber-700">Low contrast: {weak.map((pair) => `${ROLE_LABELS[pair.foreground]} on ${ROLE_LABELS[pair.background]} (${pair.ratio}:1)`).join(", ")}.</p>
            )}
          </fieldset>

          <fieldset>
            <legend className="text-sm font-semibold text-slate-900">Fonts</legend>
            <p className="text-xs text-slate-500">Cards need heading and body font files. Missing fonts stop card rendering; there is no fallback.</p>
            <ul className="mt-2 space-y-1">
              {FONT_ROLES.map((role) => {
                const font = kit?.fonts.find((candidate) => candidate.role === role);
                return (
                  <li key={role} className="flex items-center justify-between gap-2 text-xs" data-testid={`kit-font-${role}`}>
                    <span className="text-slate-700">
                      {FONT_LABELS[role]}: {font ? `${font.family} ${font.weight}` : kit?.typography[role] ? <span className="text-amber-700">{kit.typography[role]} (file missing)</span> : <span className="text-slate-400">none</span>}
                    </span>
                    <label className="cursor-pointer rounded border border-slate-300 px-2 py-0.5 font-medium text-slate-800 hover:bg-slate-50">
                      {font ? "Replace" : "Upload"}
                      <input type="file" accept=".ttf,.otf,.woff,.woff2" className="sr-only" disabled={busy} onChange={(event) => void handleFont(role, event)} />
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>

          <label className="block text-xs font-medium text-slate-800">
            Art direction
            <textarea value={draft.artDirection} maxLength={1200} rows={3} onChange={(event) => setDraft({ ...draft, artDirection: event.target.value })}
              placeholder="e.g. Warm editorial illustration, real product UI, calm composition"
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-xs font-normal" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-xs font-medium text-slate-800">
              Do (one per line)
              <textarea value={draft.dos} rows={3} onChange={(event) => setDraft({ ...draft, dos: event.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-xs font-normal" />
            </label>
            <label className="block text-xs font-medium text-slate-800">
              Avoid (one per line)
              <textarea value={draft.donts} rows={3} onChange={(event) => setDraft({ ...draft, donts: event.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-xs font-normal" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-xs font-medium text-slate-800">
              Card label
              <input value={draft.eyebrow} maxLength={40} onChange={(event) => setDraft({ ...draft, eyebrow: event.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-xs font-normal" />
            </label>
            <label className="block text-xs font-medium text-slate-800">
              Wordmark
              <input value={draft.wordmark} maxLength={60} onChange={(event) => setDraft({ ...draft, wordmark: event.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-xs font-normal" />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => void handleSave()} disabled={busy}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60">
              {save.isPending ? "Saving…" : "Save visual kit"}
            </button>
            {message && <p role={message.tone === "error" ? "alert" : "status"} className={`text-xs ${message.tone === "error" ? "text-red-700" : "text-emerald-700"}`}>{message.text}</p>}
          </div>
        </div>

        <div className="space-y-2 rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-900">Social card preview</h4>
          <label className="block text-xs text-slate-700">
            Sample title
            <input value={previewTitle} maxLength={200} onChange={(event) => setPreviewTitle(event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-xs" />
          </label>
          {previewSrc ? (
            <img src={previewSrc} alt="Social card preview from the saved kit" className="w-full rounded border border-slate-200" data-testid="kit-preview" />
          ) : kit && hasRenderFonts ? (
            <p className="rounded-md border border-dashed border-slate-300 p-6 text-center text-xs text-slate-500" role={previewQ.isError ? "alert" : "status"}>
              {previewQ.isError ? (previewQ.error instanceof Error ? previewQ.error.message : "The preview could not be rendered.") : "Rendering preview…"}
            </p>
          ) : (
            <p className="rounded-md border border-dashed border-slate-300 p-6 text-center text-xs text-slate-500">
              {kit ? "Upload heading and body fonts to preview the social card." : "Import design tokens or save the kit to get started."}
            </p>
          )}
          <p className="text-xs text-slate-500">The preview uses the saved kit. Save your changes to see them here.</p>
        </div>
      </div>
    </div>
  );
}
